// Venue Check-In page.
//
// Admin / event-creator scans a ticket QR (native camera on iOS+Android via
// @capacitor-mlkit/barcode-scanning, or paste-string fallback on web). Calls
// /api/bookings/check-in/lookup to preview, then /api/bookings/{id}/check-in
// to commit. Shows event + tier + attendee details + scan history.
//
// Routes mounted in App.js as /admin/check-in.
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { isNative } from '../lib/native';
import { scanOnce, ensureScannerPermission, isScannerSupported } from '../lib/qrScanner';
import { tapHaptic } from '../lib/native';
import { toast } from 'sonner';

const VERDICT_STYLE = {
  pending:             { bg: 'bg-emerald-50',  border: 'border-emerald-300',  text: 'text-emerald-700', icon: 'ph-check-circle',          label: 'Ready to check in' },
  checked_in:          { bg: 'bg-emerald-100', border: 'border-emerald-500',  text: 'text-emerald-800', icon: 'ph-check-fat',             label: 'Checked in ✓' },
  already_checked_in:  { bg: 'bg-amber-50',    border: 'border-amber-400',    text: 'text-amber-800',   icon: 'ph-warning-circle',        label: 'Already checked in' },
  invalid:             { bg: 'bg-rose-50',     border: 'border-rose-400',     text: 'text-rose-800',    icon: 'ph-x-circle',              label: 'Invalid ticket' },
};

function ScanResultCard({ result, onCheckin, busy }) {
  if (!result) return null;
  const tone = VERDICT_STYLE[result.status] || VERDICT_STYLE.invalid;
  const b = result.booking || {};
  const a = result.attendee || {};
  const items = b.items || [];

  return (
    <div
      data-testid="checkin-result-card"
      className={`rounded-2xl border-2 ${tone.bg} ${tone.border} p-5 shadow-sm space-y-4`}
    >
      <div className={`flex items-center gap-3 ${tone.text}`}>
        <i className={`${tone.icon} ph-fill text-3xl`}></i>
        <div>
          <p data-testid="checkin-verdict" className="text-lg font-extrabold">{tone.label}</p>
          {b.checked_in_at && (
            <p className="text-xs opacity-80">
              {new Date(b.checked_in_at).toLocaleString()}
              {b.checked_in_by ? ` · by ${b.checked_in_by}` : ''}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 space-y-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Event</p>
          <p data-testid="checkin-event-title" className="text-base font-bold text-gray-900">{b.event_title || '—'}</p>
          <p className="text-xs text-gray-500">{b.venue_name || ''}{b.event_date ? ` · ${b.event_date}` : ''}</p>
        </div>

        <div className="border-t border-dashed border-gray-200 pt-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Tickets</p>
          <ul data-testid="checkin-items" className="space-y-1">
            {items.length === 0 && <li className="text-sm text-gray-500">No items</li>}
            {items.map((it, i) => (
              <li key={i} className="flex justify-between text-sm">
                <span className="font-bold">{it.tier_name}</span>
                <span className="text-gray-600">×{it.quantity}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-dashed border-gray-200 pt-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Attendee</p>
          <p data-testid="checkin-attendee-name" className="text-sm font-bold text-gray-900">{a.name || '—'}</p>
          <p className="text-xs text-gray-500">{a.email || ''}{a.phone ? ` · ${a.phone}` : ''}</p>
        </div>
      </div>

      {result.status === 'pending' && (
        <button
          data-testid="checkin-confirm-btn"
          disabled={busy}
          onClick={onCheckin}
          className="w-full py-3 rounded-xl bg-emerald-600 text-white font-extrabold disabled:opacity-50"
        >
          {busy ? 'Checking in…' : 'Confirm check-in'}
        </button>
      )}
    </div>
  );
}

export default function CheckInPage() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);   // {status, booking, attendee}
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [manualId, setManualId] = useState('');
  const [scannerOk, setScannerOk] = useState(false);

  useEffect(() => {
    (async () => setScannerOk(await isScannerSupported()))();
  }, []);

  const lookupQR = async (qr) => {
    if (!qr) return;
    try {
      const r = await axiosInstance.post('/bookings/check-in/lookup', { qr });
      setResult(r.data);
      await tapHaptic();
    } catch (e) {
      // Hide the result card entirely on lookup errors — the toast alone is
      // clearer than rendering an empty card with em-dashes.
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail
        || (status === 404 ? 'Ticket not found' : 'Lookup failed');
      setResult(null);
      toast.error(msg);
    }
  };

  const handleScan = async () => {
    if (!isNative()) {
      toast.message('Scanner unavailable in browser', {
        description: 'Use the manual entry below, or open the app on your phone.',
      });
      return;
    }
    const perm = await ensureScannerPermission();
    if (perm !== 'granted') {
      toast.error('Camera permission required to scan tickets');
      return;
    }
    setScanning(true);
    setResult(null);
    try {
      const code = await scanOnce({ formats: ['QR_CODE'] });
      if (!code) {
        toast.message('Scan cancelled');
        return;
      }
      await lookupQR(code);
    } finally {
      setScanning(false);
    }
  };

  const handleManualLookup = async (e) => {
    e?.preventDefault?.();
    if (!manualId.trim()) return;
    setResult(null);
    await lookupQR(manualId.trim());
  };

  const handleConfirm = async () => {
    if (!result?.booking?.id) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/bookings/${result.booking.id}/check-in`);
      setResult(r.data);
      toast.success(
        r.data.status === 'already_checked_in'
          ? 'Already checked in earlier'
          : 'Checked in successfully'
      );
      await tapHaptic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Check-in failed');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setManualId('');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-nav" data-testid="checkin-page">
      <header className="bg-[#27306D] text-white px-5 pt-12 pb-6">
        <div className="flex items-center gap-3 mb-2">
          <button
            data-testid="checkin-back-btn"
            onClick={() => navigate('/admin')}
            className="text-white/80"
            aria-label="Back"
          >
            <i className="ph-bold ph-arrow-left text-xl"></i>
          </button>
          <h1 className="text-2xl font-extrabold tracking-tight">Ticket Check-In</h1>
        </div>
        <p className="text-sm text-white/70">Scan a QR at the gate or enter the booking ID manually.</p>
      </header>

      <main className="px-5 -mt-3 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
          <button
            data-testid="checkin-scan-btn"
            onClick={handleScan}
            disabled={scanning}
            className="w-full py-4 rounded-xl bg-[#E54562] text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <i className="ph-fill ph-qr-code text-xl"></i>
            {scanning ? 'Opening camera…' : (isNative() ? 'Scan ticket' : 'Scanner (open in app)')}
          </button>
          {!isNative() && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              The native camera scanner is only available in the iOS / Android app.
              Use manual entry below for now.
            </p>
          )}
          {isNative() && !scannerOk && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              Scanner module isn't ready on this device yet — first scan will install it (~2 MB on Android).
            </p>
          )}

          <form onSubmit={handleManualLookup} className="flex gap-2">
            <input
              data-testid="checkin-manual-input"
              type="text"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="Booking ID or QR text"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm font-mono"
            />
            <button
              data-testid="checkin-manual-lookup-btn"
              type="submit"
              className="px-4 py-2 rounded-xl bg-[#27306D] text-white font-bold text-sm"
            >
              Lookup
            </button>
          </form>
        </div>

        <ScanResultCard result={result} onCheckin={handleConfirm} busy={busy} />

        {result && (
          <button
            data-testid="checkin-reset-btn"
            onClick={reset}
            className="w-full py-3 rounded-xl bg-white border border-gray-200 text-gray-700 font-bold text-sm"
          >
            Scan another ticket
          </button>
        )}
      </main>
    </div>
  );
}
